import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { BACKEND_URL } from '@/config';

export default function Login() {
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [ssoTimedOut, setSsoTimedOut] = useState(false);
  // Use a ref instead of state so the guard doesn't trigger effect re-runs
  const ssoCompletedRef = useRef(false);

  const handleGHLSSO = useCallback(() => {
    // Prevent SSO from running multiple times
    if (ssoCompletedRef.current) {
      console.log('[SSO] Already completed, skipping...');
      return () => {};
    }

    const handleGHLMessage = async (event) => {
      // No origin allowlist — whitelabeled GHL accounts use custom domains.
      // The backend SSO decryption key is the real security boundary.
      console.log('[SSO] Received message from parent:', event.data, 'Origin:', event.origin);

      if (event.data && event.data.message === 'REQUEST_USER_DATA_RESPONSE') {
        console.log('[SSO] Got REQUEST_USER_DATA_RESPONSE');
        
        // Guard against duplicate processing
        if (ssoCompletedRef.current) return;
        ssoCompletedRef.current = true;
        
        const encryptedUserData = event.data.payload;
        
        if (!encryptedUserData) {
          console.error('[SSO] No encrypted data in response');
          return;
        }

        console.log('[SSO] Received encrypted SSO data from GHL. Length:', encryptedUserData.length);

        try {
          const response = await api.post('/auth/decrypt-sso', {
            encryptedData: encryptedUserData
          });

          if (response.data.ok) {
            if (response.data.session_id) {
              console.log('[SSO] 💾 Storing session_id in localStorage:', response.data.session_id.substring(0, 20) + '...');
              localStorage.setItem('pos_session_id', response.data.session_id);
              console.log('[SSO] ✅ Verification - localStorage has session_id:', localStorage.getItem('pos_session_id') ? 'YES' : 'NO');
            } else {
              console.error('[SSO] ❌ WARNING: No session_id in response!', response.data);
            }
            
            const today = new Date().toISOString().slice(0,10);
            localStorage.setItem('pos_login_date', today);
            console.log('[SSO] ✅ SSO authentication successful!');
            nav('/');
          }
        } catch (error) {
          console.error('[SSO] Authentication failed:', error);
          ssoCompletedRef.current = false; // Allow retry on error
        }
      }
    };

    window.addEventListener('message', handleGHLMessage);

    const requestUserData = () => {
      console.log('[SSO] Requesting user data from GHL parent window...');
      window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
    };

    // Request immediately, then retry every 500ms for up to 10 seconds
    requestUserData();
    const interval = setInterval(requestUserData, 500);
    const stopTimeout = setTimeout(() => {
      clearInterval(interval);
      console.log('[SSO] Stopped requesting user data after timeout');
    }, 10000);

    return () => {
      window.removeEventListener('message', handleGHLMessage);
      clearInterval(interval);
      clearTimeout(stopTimeout);
    };
  }, [nav]); // ✅ ssoCompleted removed — using ref avoids re-creating this on each SSO state change

  useEffect(() => {
    let mounted = true;
    let ssoCleanup = null;
    
    const isInIframe = window.self !== window.top;
    console.log('[Login] isInIframe:', isInIframe);
    
    if (!isInIframe) {
      console.log('[Login] Not in iframe - will show access message after delay');
      const t = setTimeout(() => {
        if (mounted) {
          console.log('[Login] Showing access message');
          setChecking(false);
        }
      }, 5000);
      return () => { mounted = false; clearTimeout(t); };
    }
    
    const sessionId = localStorage.getItem('pos_session_id');
    console.log('[Login] Checking for existing session. localStorage has session_id:', sessionId ? 'YES' : 'NO');
    
    if (!sessionId) {
      console.log('[Login] No session_id in localStorage, setting up SSO listener...');
      ssoCleanup = handleGHLSSO();
      
      const t = setTimeout(() => {
        if (mounted && !ssoCompletedRef.current) {
          console.log('[Login] SSO timeout - showing retry option');
          setChecking(false);
          setSsoTimedOut(true);
        }
      }, 12000);
      
      return () => { mounted = false; clearTimeout(t); if (typeof ssoCleanup === 'function') ssoCleanup(); };
    }
    
    // Have session_id — verify it's still valid
    api.get('/auth/me')
      .then(() => { 
        if (mounted) {
          console.log('[Login] ✅ Existing session verified, redirecting to dashboard');
          const today = new Date().toISOString().slice(0,10);
          localStorage.setItem('pos_login_date', today);
          nav('/'); 
        }
      })
      .catch((error) => {
        if (mounted) {
          console.log('[Login] ❌ Session verification failed:', error?.response?.status);
          console.log('[Login] Clearing invalid session and setting up SSO...');
          localStorage.removeItem('pos_session_id');
          
          ssoCleanup = handleGHLSSO();
          
          const t = setTimeout(() => {
            if (mounted && !ssoCompletedRef.current) {
              console.log('[Login] SSO timeout - showing retry option');
              setChecking(false);
              setSsoTimedOut(true);
            }
          }, 12000);
          
          // Store t so outer cleanup can cancel it
          mounted && (mounted = t);
        }
      });
    
    return () => { 
      mounted = false;
      if (typeof ssoCleanup === 'function') ssoCleanup();
    };
  }, [nav, handleGHLSSO]); // ✅ ssoCompleted removed — no longer causes re-runs

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <img 
            src="/castle-logo.png" 
            alt="Castle POS" 
            className="w-32 h-32 mx-auto mb-6 animate-pulse"
          />
          <div className="text-xl text-gray-300">Authenticating...</div>
        </div>
      </div>
    );
  }

  // If SSO timed out in iframe, show retry option
  if (ssoTimedOut) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8">
          <img 
            src="/castle-logo.png" 
            alt="Castle POS" 
            className="w-24 h-24 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-center mb-6 text-slate-900">Authentication Timeout</h1>
          <div className="text-center">
            <p className="text-gray-600 mb-6">
              Unable to complete authentication. This may be due to a session timeout or network issue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-block px-8 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors shadow-lg"
            >
              Retry Authentication
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8">
        <img 
          src="/castle-logo.png" 
          alt="Castle POS" 
          className="w-24 h-24 mx-auto mb-6"
        />
        <h1 className="text-3xl font-bold text-center mb-6 text-slate-900">Castle POS</h1>
        <div className="text-center">
          <p className="text-gray-600 mb-6">
            This application requires authorized access.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            Please contact us for access.
          </p>
          <a 
            href="https://www.castlepos.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block px-8 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors shadow-lg"
          >
            Visit Castle POS
          </a>
        </div>
      </div>
    </div>
  );
}
